/**
 * StockMovementWizard — Receive Stock / Issue Stock entry-point.
 *
 * Parameterised by `direction`:
 *   - `inbound`  → goods receipt (vendor + PO). Submits to procurement.
 *   - `outbound` → sales shipment (customer + SO). Submits to sales.
 *
 * Both variants share the same scaffold:
 *   1. Warehouse + party (vendor or customer)
 *   2. Pick an open PO/SO  OR  switch to the "no document" branch
 *      (reason code → free-form line items)
 *   3. Confirm + submit
 *
 * On success it navigates to the new receipt/shipment detail page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowUpFromLine,
  Building2,
  CheckCircle2,
  Loader2,
  Package,
  Plus,
  Trash2,
  Warehouse as WarehouseIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { PROC_UNMATCHED_RECEIPT_REASON_VALUES } from '../../../generated/config-enums';
// eslint-disable-next-line no-restricted-imports -- multi-step wizard needs the raw Dialog primitives
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { AddLineButton, ConfirmDialog } from '../../../shared/components/common';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import { CompanyFormField } from '../../../shared/components/multi-company/CompanyFormField';
import { useMyCompanies } from '../../../shared/users/hooks/useCompanies';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { WarehousePicker } from '../../../shared/components/inventory/WarehousePicker';
import { useWarehouses, useLocations } from '../hooks/useWarehouses';
import type { Location } from '../types';
import {
  useCreateMatchedReceipt,
  useConfirmMatchedReceipt,
  useCreateMatchedShipment,
  useCreateUnmatchedReceipt,
  useCreateUnmatchedShipment,
  useOpenPurchaseOrdersForVendor,
  useOpenSalesOrdersForCustomer,
  getDraftReceiptId,
} from '../hooks/useStockMovementWizard';
import type {
  WizardOpenPurchaseOrder,
  WizardOpenSalesOrder,
} from '../services/inventoryApi';
import { LocalizedDateDisplay } from '../../../shared/components/LocalizedDateDisplay';
import { navigateToEntity } from '../../../shared/utils/entityUrls';
import { ProductReferencePicker } from '../../master_data/products/components/ProductReferencePicker';
import { getErrorCode } from '../../../shared/utils/errors';

export type StockMovementDirection = 'inbound' | 'outbound';

interface StockMovementWizardProps {
  open: boolean;
  onClose: () => void;
  direction: StockMovementDirection;
  /** Optional warehouse pre-selection (e.g., when launched from a warehouse detail page). */
  defaultWarehouseId?: string;
}

type WizardStep = 'context' | 'document' | 'unmatched-items' | 'review';

interface LineDraft {
  // Stable client-side id so React keys survive reorder/remove without remounts.
  draftId: string;
  // For matched flow: po_item_id / sales_order_item_id, captured for downstream use.
  source_line_id?: string;
  product_id: string;
  variant_id?: string | null;
  description: string;
  quantity: number;
  quantity_outstanding?: number;
  unit_cost: number;
  unit_price: number;
  uom: string | null;
  /** Bin within the warehouse — putaway (inbound) / pick-from (outbound). */
  location_id?: string | null;
}

let _draftIdCounter = 0;
function makeDraftId(): string {
  _draftIdCounter += 1;
  return `ld-${Date.now()}-${_draftIdCounter}`;
}

// Single-sourced from shared/config/proc_unmatched_receipt_reasons.config.json
// (matches proc_goods_receipts.reason_code) — never re-hardcode the value set.
const INBOUND_REASON_CODES = PROC_UNMATCHED_RECEIPT_REASON_VALUES;

const OUTBOUND_REASON_CODES = [
  'sample_shipment',
  'warranty_replacement',
  'internal_use',
  'customer_pickup_no_so',
  'other',
] as const;

export function StockMovementWizard({
  open,
  onClose,
  direction,
  defaultWarehouseId,
}: StockMovementWizardProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const isInbound = direction === 'inbound';

  // ---- Form state ----
  const [step, setStep] = useState<WizardStep>('context');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [partyId, setPartyId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [isUnmatchedBranch, setIsUnmatchedBranch] = useState(false);
  const [reasonCode, setReasonCode] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [pendingOverReceiptId, setPendingOverReceiptId] = useState<string | null>(null);

  // ---- Data ----
  const { data: warehouses = [] } = useWarehouses({ is_active: true });
  const { data: locations = [] } = useLocations(warehouseId);
  const { data: openPOs = [], isFetching: isFetchingPOs } =
    useOpenPurchaseOrdersForVendor(isInbound && partyId ? partyId : '');
  const { data: openSOs = [], isFetching: isFetchingSOs } =
    useOpenSalesOrdersForCustomer(!isInbound && partyId ? partyId : '');
  const { data: myCompanies } = useMyCompanies();
  const companies = useMemo(() => myCompanies?.data ?? [], [myCompanies]);

  // ---- Mutations ----
  const createMatchedReceipt = useCreateMatchedReceipt();
  const confirmMatchedReceipt = useConfirmMatchedReceipt();
  const createUnmatchedReceipt = useCreateUnmatchedReceipt();
  const createMatchedShipment = useCreateMatchedShipment();
  const createUnmatchedShipment = useCreateUnmatchedShipment();

  // ---- Reset on open ----
  useEffect(() => {
    if (open) {
      setStep('context');
      setWarehouseId(defaultWarehouseId ?? '');
      setPartyId('');
      setCompanyId(companies.length === 1 ? companies[0].id : '');
      setSelectedDocumentId('');
      setIsUnmatchedBranch(false);
      setReasonCode('');
      setNotes('');
      setLines([]);
      setSubmitting(false);
      setPendingOverReceiptId(null);
    }
    // `companies` deliberately excluded from deps: this effect resets state on
    // dialog open, not on every React Query background refetch of the companies
    // list (which would silently wipe in-progress user input mid-wizard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultWarehouseId]);

  const reasonCodes = isInbound ? INBOUND_REASON_CODES : OUTBOUND_REASON_CODES;
  const openDocuments = isInbound ? openPOs : openSOs;
  const isFetchingDocs = isInbound ? isFetchingPOs : isFetchingSOs;

  // ---- Navigation ----
  const goNext = () => {
    if (step === 'context') {
      setStep('document');
    } else if (step === 'document') {
      if (isUnmatchedBranch) {
        setStep('unmatched-items');
      } else {
        // Selecting a doc preloads its lines.
        const doc = openDocuments.find((d) => d.id === selectedDocumentId);
        if (doc) {
          setLines(buildLinesFromDocument(doc, isInbound));
          setStep('review');
        }
      }
    } else if (step === 'unmatched-items') {
      setStep('review');
    }
  };

  const goBack = () => {
    if (step === 'review') {
      setStep(isUnmatchedBranch ? 'unmatched-items' : 'document');
    } else if (step === 'unmatched-items') {
      setStep('document');
    } else if (step === 'document') {
      setStep('context');
    }
  };

  // ---- Validation gates ----
  const contextReady =
    warehouseId !== '' && partyId !== '' && (companies.length <= 1 || companyId !== '');
  const documentReady = isUnmatchedBranch
    ? reasonCode !== ''
    : selectedDocumentId !== '';
  const unmatchedItemsReady =
    lines.length > 0 &&
    lines.every((ln) => ln.product_id !== '' && ln.quantity > 0);
  const reviewReady = lines.length > 0 && lines.every((ln) => ln.quantity > 0);

  // ---- Submit ----
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isInbound && isUnmatchedBranch) {
        const result = await createUnmatchedReceipt.mutateAsync({
          warehouse_id: warehouseId,
          vendor_id: partyId,
          company_id: companyId,
          reason_code: reasonCode,
          notes: notes || null,
          items: lines.map((ln) => ({
            product_id: ln.product_id,
            variant_id: ln.variant_id ?? null,
            quantity_received: ln.quantity,
            unit_cost: ln.unit_cost,
            location_id: ln.location_id ?? null,
            description: ln.description || null,
          })),
        });
        toast.success(t('inventory:wizard.receiveSuccess'));
        onClose();
        navigateToEntity(navigate, 'proc_goods_receipts', result.id);
      } else if (isInbound) {
        // Matched: create + confirm the receipt against the selected PO.
        const receipt = await createMatchedReceipt.mutateAsync({
          purchase_order_id: selectedDocumentId,
          warehouse_id: warehouseId,
          notes: notes || undefined,
          items: lines.map((ln) => ({
            po_item_id: ln.source_line_id ?? '',
            quantity_received: ln.quantity,
            unit_cost: ln.unit_cost,
            location_id: ln.location_id ?? null,
          })),
        });
        toast.success(t('inventory:wizard.receiveSuccess'));
        onClose();
        navigateToEntity(navigate, 'proc_goods_receipts', receipt.id);
      } else if (isUnmatchedBranch) {
        const result = await createUnmatchedShipment.mutateAsync({
          warehouse_id: warehouseId,
          customer_id: partyId,
          company_id: companyId,
          reason_code: reasonCode,
          notes: notes || null,
          items: lines.map((ln) => ({
            product_id: ln.product_id,
            variant_id: ln.variant_id ?? null,
            quantity: ln.quantity,
            unit_price: ln.unit_price,
            unit_cost: ln.unit_cost,
            location_id: ln.location_id ?? null,
            description: ln.description || null,
          })),
        });
        toast.success(t('inventory:wizard.issueSuccess'));
        onClose();
        // Shipments don't have a standalone detail page — surface them on
        // their parent sales order page (placeholder SO for the unmatched flow).
        if (result.sales_order_id) {
          navigateToEntity(navigate, 'sales_orders', result.sales_order_id);
        }
      } else {
        // Matched outbound: create a pending shipment against the SO, then ship it.
        // The shipment id isn't used — we navigate to the originating SO below.
        await createMatchedShipment.mutateAsync({
          order_id: selectedDocumentId,
          warehouse_id: warehouseId,
          lines: lines.map((ln) => ({
            sales_order_item_id: ln.source_line_id ?? '',
            quantity: ln.quantity,
            location_id: ln.location_id ?? null,
          })),
        });
        toast.success(t('inventory:wizard.issueSuccess'));
        onClose();
        // Matched flow: go to the originating sales order detail page.
        navigateToEntity(navigate, 'sales_orders', selectedDocumentId);
      }
    } catch (error) {
      const draftReceiptId = getDraftReceiptId(error);
      if (
        draftReceiptId &&
        getErrorCode(error) === 'OVER_RECEIPT_EXCEEDS_TOLERANCE'
      ) {
        setPendingOverReceiptId(draftReceiptId);
      }
      // The global mutation-error handler shows the specific translated toast.
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step content ----
  const renderStep = () => {
    switch (step) {
      case 'context':
        return (
          <ContextStep
            isInbound={isInbound}
            warehouseId={warehouseId}
            onWarehouseChange={setWarehouseId}
            partyId={partyId}
            onPartyChange={setPartyId}
            companyId={companyId}
            onCompanyChange={setCompanyId}
          />
        );
      case 'document':
        return (
          <DocumentStep
            isInbound={isInbound}
            openDocuments={openDocuments}
            isFetching={isFetchingDocs}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={(id) => {
              setSelectedDocumentId(id);
              setIsUnmatchedBranch(false);
            }}
            isUnmatchedBranch={isUnmatchedBranch}
            onSwitchToUnmatched={() => {
              setIsUnmatchedBranch(true);
              setSelectedDocumentId('');
            }}
            reasonCode={reasonCode}
            onReasonChange={setReasonCode}
            reasonCodes={reasonCodes}
          />
        );
      case 'unmatched-items':
        return (
          <UnmatchedItemsStep
            isInbound={isInbound}
            lines={lines}
            onLinesChange={setLines}
            locations={locations}
            notes={notes}
            onNotesChange={setNotes}
          />
        );
      case 'review':
        return (
          <ReviewStep
            isInbound={isInbound}
            warehouses={warehouses}
            warehouseId={warehouseId}
            partyId={partyId}
            isUnmatchedBranch={isUnmatchedBranch}
            selectedDocumentId={selectedDocumentId}
            openDocuments={openDocuments}
            reasonCode={reasonCode}
            lines={lines}
            onLinesChange={setLines}
            locations={locations}
            notes={notes}
            onNotesChange={setNotes}
          />
        );
    }
  };

  const stepIndex =
    step === 'context' ? 1 : step === 'document' ? 2 : step === 'unmatched-items' ? 3 : step === 'review' ? (isUnmatchedBranch ? 4 : 3) : 1;
  const totalSteps = isUnmatchedBranch ? 4 : 3;

  const TitleIcon = isInbound ? ArrowDownToLine : ArrowUpFromLine;
  const title = isInbound ? t('inventory:wizard.receiveStock') : t('inventory:wizard.issueStock');

  const titleNode = (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <TitleIcon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div>{title}</div>
        <div className="text-xs text-muted-foreground font-normal">
          {t('inventory:wizard.stepCount', { current: stepIndex, total: totalSteps })}
        </div>
      </div>
    </div>
  );

  const overReceiptConfirmDialog = (
    <ConfirmDialog
      open={pendingOverReceiptId !== null}
      onClose={() => setPendingOverReceiptId(null)}
      title={t('inventory:wizard.overReceiptConfirmTitle')}
      message={t('inventory:wizard.overReceiptConfirmMessage')}
      confirmLabel={t('inventory:wizard.overReceiptConfirmAction')}
      isLoading={confirmMatchedReceipt.isPending}
      onConfirm={async () => {
        if (!pendingOverReceiptId) return;
        const receipt = await confirmMatchedReceipt.mutateAsync(pendingOverReceiptId);
        setPendingOverReceiptId(null);
        toast.success(t('inventory:wizard.receiveSuccess'));
        onClose();
        navigateToEntity(navigate, 'proc_goods_receipts', receipt.id);
      }}
      destructive={false}
    />
  );

  const footer = (
    <div className="flex items-center justify-between gap-3 pt-4 border-t mt-4">
      <Button variant="ghost" onClick={step === 'context' ? onClose : goBack} disabled={submitting}>
        {step === 'context' ? (
          t('common:app.cancel')
        ) : (
          <>
            <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
            {t('common:app.back')}
          </>
        )}
      </Button>
      {step === 'review' ? (
        <Button onClick={handleSubmit} disabled={!reviewReady || submitting}>
          {submitting ? (
            <Loader2 className="h-4 w-4 me-2 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4 me-2" />
          )}
          {isInbound ? t('inventory:wizard.confirmReceipt') : t('inventory:wizard.confirmShipment')}
        </Button>
      ) : (
        <Button
          onClick={goNext}
          disabled={
            (step === 'context' && !contextReady) ||
            (step === 'document' && !documentReady) ||
            (step === 'unmatched-items' && !unmatchedItemsReady)
          }
        >
          {t('common:app.next')}
          <ArrowRight className="h-4 w-4 ms-1 rtl:rotate-180" />
        </Button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
          <SheetContent
            side="bottom"
            className="flex max-h-[92dvh] flex-col rounded-t-xl p-0"
            hideCloseButton
            aria-describedby={undefined}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            </div>
            <SheetHeader className="px-4 pb-3">
              <SheetTitle className="text-start">{titleNode}</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4">{renderStep()}</div>
            <div className="px-4 pb-4">{footer}</div>
          </SheetContent>
        </Sheet>
        {overReceiptConfirmDialog}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className={cn('flex max-h-[90vh] flex-col sm:max-w-2xl')} aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{titleNode}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 pr-1 -mr-1">{renderStep()}</div>
          {footer}
        </DialogContent>
      </Dialog>
      {overReceiptConfirmDialog}
    </>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function buildLinesFromDocument(
  doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder,
  isInbound: boolean,
): LineDraft[] {
  if (isInbound) {
    const po = doc as WizardOpenPurchaseOrder;
    return po.items.map((it) => ({
      draftId: makeDraftId(),
      source_line_id: it.po_item_id,
      product_id: it.product_id ?? '',
      variant_id: it.variant_id,
      description: it.description ?? '',
      quantity: it.quantity_outstanding,
      quantity_outstanding: it.quantity_outstanding,
      unit_cost: it.unit_cost,
      unit_price: 0,
      uom: it.uom,
    }));
  }
  const so = doc as WizardOpenSalesOrder;
  return so.items.map((it) => ({
    draftId: makeDraftId(),
    source_line_id: it.sales_order_item_id,
    product_id: it.product_id ?? '',
    variant_id: it.variant_id,
    description: it.description ?? '',
    quantity: it.quantity_outstanding,
    quantity_outstanding: it.quantity_outstanding,
    unit_cost: 0,
    unit_price: it.unit_price,
    uom: it.uom,
  }));
}

// ============================================================================
// Step 1: Context (warehouse + party)
// ============================================================================

interface ContextStepProps {
  isInbound: boolean;
  warehouseId: string;
  onWarehouseChange: (id: string) => void;
  partyId: string;
  onPartyChange: (id: string) => void;
  companyId: string;
  onCompanyChange: (id: string) => void;
}

function ContextStep({
  isInbound,
  warehouseId,
  onWarehouseChange,
  partyId,
  onPartyChange,
  companyId,
  onCompanyChange,
}: ContextStepProps) {
  const { t } = useTranslation(['inventory', 'common']);

  return (
    <div className="space-y-4 py-2">
      <CompanyFormField value={companyId} onChange={onCompanyChange} />

      <div className="space-y-2">
        <Label>
          <span className="inline-flex items-center gap-1.5">
            <WarehouseIcon className="h-4 w-4 text-muted-foreground" />
            {t('inventory:wizard.warehouse')}
          </span>{' '}
          <span className="text-destructive">*</span>
        </Label>
        <WarehousePicker
          value={warehouseId}
          onChange={onWarehouseChange}
          placeholder={t('common:app.select')}
        />
      </div>

      <div className="space-y-2">
        <Label>
          <span className="inline-flex items-center gap-1.5">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {isInbound
              ? t('inventory:wizard.vendor')
              : t('inventory:wizard.customer')}
          </span>{' '}
          <span className="text-destructive">*</span>
        </Label>
        <OrganizationPicker
          value={partyId}
          onChange={onPartyChange}
          organizationRelationshipFilter={isInbound ? 'vendor' : 'customer'}
          placeholder={
            isInbound
              ? t('inventory:wizard.vendorPlaceholder')
              : t('inventory:wizard.customerPlaceholder')
          }
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {isInbound
          ? t('inventory:wizard.contextHintInbound')
          : t('inventory:wizard.contextHintOutbound')}
      </p>
    </div>
  );
}

// ============================================================================
// Step 2: Document selection
// ============================================================================

interface DocumentStepProps {
  isInbound: boolean;
  openDocuments: (WizardOpenPurchaseOrder | WizardOpenSalesOrder)[];
  isFetching: boolean;
  selectedDocumentId: string;
  onSelectDocument: (id: string) => void;
  isUnmatchedBranch: boolean;
  onSwitchToUnmatched: () => void;
  reasonCode: string;
  onReasonChange: (code: string) => void;
  reasonCodes: readonly string[];
}

function DocumentStep({
  isInbound,
  openDocuments,
  isFetching,
  selectedDocumentId,
  onSelectDocument,
  isUnmatchedBranch,
  onSwitchToUnmatched,
  reasonCode,
  onReasonChange,
  reasonCodes,
}: DocumentStepProps) {
  const { t } = useTranslation(['inventory', 'common']);

  const labelFor = (doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder) =>
    'po_number' in doc ? doc.po_number : doc.order_number;
  const dateFor = (doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder) =>
    'expected_delivery_date' in doc
      ? doc.expected_delivery_date
      : doc.requested_delivery_date;
  const totalQty = (doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder) =>
    doc.items.reduce((s, it) => s + it.quantity_outstanding, 0);

  return (
    <div className="space-y-4 py-2">
      <div>
        <h3 className="text-sm font-semibold mb-2">
          {isInbound
            ? t('inventory:wizard.openPurchaseOrders')
            : t('inventory:wizard.openSalesOrders')}
        </h3>
        {isFetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : openDocuments.length === 0 ? (
          <Card className="bg-muted/30">
            <CardContent className="py-6 text-center">
              <p className="text-sm text-muted-foreground">
                {isInbound
                  ? t('inventory:wizard.noOpenPOs')
                  : t('inventory:wizard.noOpenSOs')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {openDocuments.map((doc) => {
              const isSelected = !isUnmatchedBranch && selectedDocumentId === doc.id;
              const dt = dateFor(doc);
              return (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => onSelectDocument(doc.id)}
                  className={cn(
                    'w-full text-start p-3 rounded-lg border transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium">{labelFor(doc)}</span>
                    <Badge variant="outline" className="shrink-0">
                      {t('inventory:wizard.outstandingUnits', {
                        count: totalQty(doc),
                      })}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {dt && (
                      <span>
                        {t('inventory:wizard.expected')}{' '}
                        <LocalizedDateDisplay dateOnly date={dt} />
                      </span>
                    )}
                    <span>
                      {t('inventory:wizard.lineCount', { count: doc.items.length })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Unmatched branch */}
      <div className="pt-3 border-t">
        <button
          type="button"
          onClick={onSwitchToUnmatched}
          className={cn(
            'w-full text-start p-3 rounded-lg border transition-colors',
            isUnmatchedBranch
              ? 'border-warning bg-warning/5'
              : 'border-dashed border-border hover:bg-accent',
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            <Plus className="h-4 w-4" />
            <span className="font-medium">
              {isInbound
                ? t('inventory:wizard.noPOSwitch')
                : t('inventory:wizard.noSOSwitch')}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {isInbound
              ? t('inventory:wizard.noPOSwitchHint')
              : t('inventory:wizard.noSOSwitchHint')}
          </p>
        </button>

        {isUnmatchedBranch && (
          <div className="mt-3 space-y-2">
            <Label>
              {t('inventory:wizard.reasonCode')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <Select value={reasonCode} onValueChange={onReasonChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('common:app.select')} />
              </SelectTrigger>
              <SelectContent>
                {reasonCodes.map((rc) => (
                  <SelectItem key={rc} value={rc}>
                    {t(`inventory:wizard.reason.${rc}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Step 3: Unmatched items (free-form line entry)
// ============================================================================

interface UnmatchedItemsStepProps {
  isInbound: boolean;
  lines: LineDraft[];
  onLinesChange: (lines: LineDraft[]) => void;
  locations: Location[];
  notes: string;
  onNotesChange: (notes: string) => void;
}

function UnmatchedItemsStep({
  isInbound,
  lines,
  onLinesChange,
  locations,
  notes,
  onNotesChange,
}: UnmatchedItemsStepProps) {
  const { t } = useTranslation(['inventory', 'common']);

  const addLine = () => {
    onLinesChange([
      ...lines,
      {
        draftId: makeDraftId(),
        product_id: '',
        description: '',
        quantity: 1,
        unit_cost: 0,
        unit_price: 0,
        uom: null,
      },
    ]);
  };

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    onLinesChange(lines.map((ln, i) => (i === index ? { ...ln, ...patch } : ln)));
  };

  const removeLine = (index: number) => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 py-2">
      <h3 className="text-sm font-semibold">{t('inventory:wizard.items')}</h3>

      {lines.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="py-6 text-center">
            <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('inventory:wizard.emptyItems')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {lines.map((ln, index) => (
            <UnmatchedLineCard
              key={ln.draftId}
              line={ln}
              isInbound={isInbound}
              locations={locations}
              onChange={(patch) => updateLine(index, patch)}
              onRemove={() => removeLine(index)}
            />
          ))}
        </div>
      )}

      <AddLineButton label={t('inventory:wizard.addLine')} onClick={addLine} />

      <div className="space-y-2">
        <Label htmlFor="wizard-notes">{t('inventory:wizard.notes')}</Label>
        <Textarea
          id="wizard-notes"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={t('inventory:wizard.notesPlaceholder')}
          rows={2}
        />
      </div>
    </div>
  );
}

interface UnmatchedLineCardProps {
  line: LineDraft;
  isInbound: boolean;
  locations: Location[];
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove: () => void;
}

function UnmatchedLineCard({ line, isInbound, locations, onChange, onRemove }: UnmatchedLineCardProps) {
  const { t } = useTranslation(['inventory', 'common']);

  return (
    <Card>
      <CardContent className="p-3 space-y-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <ProductReferencePicker
              value={
                line.product_id
                  ? {
                      product_id: line.product_id,
                      offering_id: null,
                      variant_id: line.variant_id ?? null,
                      packaging_id: null,
                      lot_id: null,
                      configuration_selections: [],
                    }
                  : null
              }
              onChange={(ref) => {
                if (!ref) {
                  onChange({ product_id: '', variant_id: null });
                } else {
                  onChange({
                    product_id: ref.product_id ?? '',
                    variant_id: ref.variant_id ?? null,
                  });
                }
              }}
              productOnly
              showLabels={false}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="shrink-0 h-11 w-11"
            aria-label={t('inventory:wizard.removeLine')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{t('inventory:wizard.quantity')}</Label>
            <Input
              size="sm"
              type="number"
              min={0}
              step="any"
              value={line.quantity}
              onChange={(e) => onChange({ quantity: Math.max(0, parseFloat(e.target.value) || 0) })}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {isInbound
                ? t('inventory:wizard.unitCostCents')
                : t('inventory:wizard.unitPriceCents')}
            </Label>
            <Input
              size="sm"
              type="number"
              min={0}
              value={isInbound ? line.unit_cost : line.unit_price}
              onChange={(e) =>
                onChange(
                  isInbound
                    ? { unit_cost: Math.max(0, parseInt(e.target.value, 10) || 0) }
                    : { unit_price: Math.max(0, parseInt(e.target.value, 10) || 0) },
                )
              }
            />
          </div>
        </div>
        {locations.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs">
              {isInbound ? t('inventory:wizard.putawayLocation') : t('inventory:wizard.pickLocation')}
            </Label>
            <Select
              value={line.location_id ?? ''}
              onValueChange={(v) => onChange({ location_id: v || null })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('common:app.none')} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Step 4: Review & confirm
// ============================================================================

interface ReviewStepProps {
  isInbound: boolean;
  warehouses: { id: string; name: string; code: string }[];
  warehouseId: string;
  partyId: string;
  isUnmatchedBranch: boolean;
  selectedDocumentId: string;
  openDocuments: (WizardOpenPurchaseOrder | WizardOpenSalesOrder)[];
  reasonCode: string;
  lines: LineDraft[];
  onLinesChange: (lines: LineDraft[]) => void;
  locations: Location[];
  notes: string;
  onNotesChange: (notes: string) => void;
}

function ReviewStep({
  isInbound,
  warehouses,
  warehouseId,
  isUnmatchedBranch,
  selectedDocumentId,
  openDocuments,
  reasonCode,
  lines,
  onLinesChange,
  locations,
  notes,
  onNotesChange,
}: ReviewStepProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const warehouse = warehouses.find((w) => w.id === warehouseId);
  const matchedDoc = useMemo(
    () => openDocuments.find((d) => d.id === selectedDocumentId),
    [openDocuments, selectedDocumentId],
  );

  const updateQty = (index: number, qty: number) => {
    onLinesChange(lines.map((ln, i) => (i === index ? { ...ln, quantity: Math.max(0, qty) } : ln)));
  };

  const updateLocation = (index: number, locationId: string | null) => {
    onLinesChange(lines.map((ln, i) => (i === index ? { ...ln, location_id: locationId } : ln)));
  };

  return (
    <div className="space-y-4 py-2">
      {/* Summary */}
      <Card className="bg-muted/30">
        <CardContent className="p-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('inventory:wizard.warehouse')}</span>
            <span className="font-medium">{warehouse?.name}</span>
          </div>
          {isUnmatchedBranch ? (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('inventory:wizard.reasonCode')}</span>
              <Badge variant="outline" className="font-normal">
                {t(`inventory:wizard.reason.${reasonCode}`)}
              </Badge>
            </div>
          ) : matchedDoc ? (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {isInbound ? t('inventory:wizard.po') : t('inventory:wizard.so')}
              </span>
              <span className="font-medium">
                {'po_number' in matchedDoc ? matchedDoc.po_number : matchedDoc.order_number}
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Lines with editable quantities */}
      <div>
        <h3 className="text-sm font-semibold mb-2">{t('inventory:wizard.lines')}</h3>
        <div className="space-y-2">
          {lines.map((ln, index) => (
            <Card key={ln.draftId}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {ln.description || t('inventory:wizard.untitledLine')}
                    </p>
                    {ln.quantity_outstanding !== undefined && (
                      <p className="text-xs text-muted-foreground">
                        {t('inventory:wizard.outstanding')}: {ln.quantity_outstanding}
                      </p>
                    )}
                  </div>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    className="w-20 sm:w-24 h-11"
                    value={ln.quantity}
                    onChange={(e) => updateQty(index, parseFloat(e.target.value) || 0)}
                  />
                </div>
                {locations.length > 0 && (
                  <div className="ms-7 space-y-1.5">
                    <Label className="text-xs">
                      {isInbound
                        ? t('inventory:wizard.putawayLocation')
                        : t('inventory:wizard.pickLocation')}
                    </Label>
                  <Select
                    value={ln.location_id ?? ''}
                    onValueChange={(v) => updateLocation(index, v || null)}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isInbound
                            ? t('inventory:wizard.putawayLocation')
                            : t('inventory:wizard.pickLocation')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Notes — visible only in matched flow; unmatched edits notes on its own step */}
      {!isUnmatchedBranch && (
        <div className="space-y-2">
          <Label htmlFor="review-notes">{t('inventory:wizard.notes')}</Label>
          <Textarea
            id="review-notes"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder={t('inventory:wizard.notesPlaceholder')}
            rows={2}
          />
        </div>
      )}
    </div>
  );
}
